import type { Permission } from "@webyonetim/contracts";
import * as DropdownMenu from "radix-ui/dropdown-menu";
import * as Tooltip from "radix-ui/tooltip";
import {
  Activity,
  BookOpen,
  CloudCog,
  FolderKanban,
  Database,
  KeyRound,
  ScrollText,
  Server,
  ShieldCheck,
  Users,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import { NavLink } from "react-router";

interface NavEntry {
  readonly Icon: LucideIcon;
  readonly label: string;
  readonly permission?: Permission;
  readonly ownerOnly?: boolean;
  readonly to: string;
}

const mainEntries: readonly NavEntry[] = [
  { Icon: Activity, label: "Genel Bakış", to: "/genel-bakis" },
  { Icon: FolderKanban, label: "Projeler", to: "/projeler" },
  { Icon: Database, label: "Veritabanları", permission: "databases:read", to: "/veritabanlari" },
  { Icon: Server, label: "Sunucular", to: "/sunucular" },
];

const managementEntries: readonly NavEntry[] = [
  { Icon: CloudCog, label: "Sağlayıcılar", ownerOnly: true, to: "/ayarlar/saglayicilar" },
  { Icon: Users, label: "Ekip", permission: "team:read", to: "/ekip" },
  {
    Icon: KeyRound,
    label: "API Anahtarları",
    permission: "api_keys:read",
    to: "/ayarlar/api-anahtarlari",
  },
  { Icon: ShieldCheck, label: "Güvenlik", to: "/ayarlar/guvenlik" },
  { Icon: ScrollText, label: "Denetim Kaydı", permission: "audit:read", to: "/denetim-kaydi" },
];

function NavigationLink({
  entry,
  onNavigate,
}: {
  readonly entry: NavEntry;
  readonly onNavigate?: (() => void) | undefined;
}) {
  const link = (
    <NavLink aria-label={entry.label} className="rail__link" onClick={onNavigate} to={entry.to}>
      <entry.Icon aria-hidden="true" size={20} strokeWidth={1.75} />
      <span>{entry.label}</span>
    </NavLink>
  );
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content className="card floating-card" side="right">
          {entry.label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export function Navigation({
  displayName,
  installationLabel,
  isOwner,
  onLogout,
  onNavigate,
  permissions,
}: {
  readonly displayName: string;
  readonly installationLabel: string;
  readonly isOwner: boolean;
  readonly onLogout: () => void;
  readonly onNavigate?: (() => void) | undefined;
  readonly permissions: readonly Permission[];
}) {
  const allowed = (entry: NavEntry) =>
    (entry.permission === undefined || permissions.includes(entry.permission)) &&
    (entry.ownerOnly !== true || isOwner);
  return (
    <Tooltip.Provider delayDuration={250}>
      <NavLink
        aria-label="WebYönetim genel bakış"
        className="rail__brand"
        onClick={onNavigate}
        to="/genel-bakis"
      >
        <strong>WebYönetim</strong>
        <span>{installationLabel}</span>
      </NavLink>
      <nav aria-label="Ana gezinme" className="rail__nav">
        <span className="rail__group-label">Ana</span>
        {mainEntries.filter(allowed).map((entry) => (
          <NavigationLink entry={entry} key={entry.to} onNavigate={onNavigate} />
        ))}
        <span className="rail__group-label">Yönetim</span>
        {managementEntries.filter(allowed).map((entry) => (
          <NavigationLink entry={entry} key={entry.to} onNavigate={onNavigate} />
        ))}
      </nav>
      <div className="rail__footer">
        <a className="rail__link" href="/api/v1/openapi.json" rel="noopener" target="_blank">
          <BookOpen aria-hidden="true" size={20} strokeWidth={1.75} />
          <span>API sürümü</span>
        </a>
        <div className="rail__link">
          <Wifi aria-hidden="true" size={20} strokeWidth={1.75} />
          <span>Bağlı</span>
        </div>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="rail__link" type="button">
              <Users aria-hidden="true" size={20} strokeWidth={1.75} />
              <span>{displayName}</span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="card floating-card" side="right">
              <DropdownMenu.Item asChild>
                <NavLink
                  className="button button--quiet"
                  onClick={onNavigate}
                  to="/ayarlar/guvenlik"
                >
                  Hesap güvenliği
                </NavLink>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <button className="button button--quiet" onClick={onLogout} type="button">
                  Çıkış yap
                </button>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </Tooltip.Provider>
  );
}
